import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Sun, CloudRain, CloudSun as CloudSunIcon, Cloud, Receipt, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";

const CATS = ["", "U6", "U8", "U10", "U12", "U14"];
const STATUS_STYLE = { scheduled: "bg-blue-100 text-blue-700", ongoing: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700", completed: "bg-emerald-100 text-emerald-700" };
const TYPE_KEYS = { training: "typeTraining", friendly: "typeFriendly", match: "typeMatch", school: "typeSchool" };

function WeatherIcon({ code }) {
  if ([61, 63, 65, 80, 81, 82, 95, 96, 99, 51, 53, 55].includes(code)) return <CloudRain className="text-blue-500" size={28} />;
  if ([0, 1].includes(code)) return <Sun className="text-amber-500" size={28} />;
  if (code === 2) return <CloudSunIcon className="text-amber-400" size={28} />;
  return <Cloud className="text-slate-400" size={28} />;
}

export default function Schedule() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [weather, setWeather] = useState(null);
  const [weatherErr, setWeatherErr] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [inquiryMsg, setInquiryMsg] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [playersList, setPlayersList] = useState([]);
  const [pAtt, setPAtt] = useState({});
  const [statsForm, setStatsForm] = useState({});
  const [remindLinks, setRemindLinks] = useState(null);
  const [myPlayers, setMyPlayers] = useState([]);
  const [rsvpState, setRsvpState] = useState({});
  const [plans, setPlans] = useState([]);
  const [notifySel, setNotifySel] = useState({});
  const [notifyLinks, setNotifyLinks] = useState(null);
  const isStaff = ["admin", "staff"].includes(user.role);

  useEffect(() => { api.get("/training-plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  const daysUntil = (d) => Math.ceil((new Date(d) - new Date(new Date().toDateString())) / 86400000);
  const EMPTY = { type: "training", title: "", category: "", date: "", start_time: "", end_time: "", location_name: "", map_embed_url: "", notes: "", plan: "" };
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const r = await api.get("/schedules");
    setItems(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const openDetail = async (s) => {
    setSelected(s);
    setWeather(null);
    setWeatherErr("");
    setInquiryMsg("");
    const att = {};
    (s.coach_attendance || []).forEach((r) => { att[r.user_id] = r.status === "present"; });
    setAttendance(att);
    const pa = {};
    (s.player_attendance || []).forEach((r) => { pa[r.player_id] = !!r.present; });
    setPAtt(pa);
    setRemindLinks(null);
    setNotifyLinks(null);
    setNotifySel({});
    const rs = {};
    (s.rsvps || []).forEach((r) => { rs[r.player_id] = r.status; });
    setRsvpState(rs);
    if (user.role === "parent") {
      try {
        const pl = await api.get("/players");
        setMyPlayers(pl.data);
      } catch { /* ignore */ }
    }
    try {
      const w = await api.get(`/schedules/${s.schedule_id}/weather`);
      setWeather(w.data);
    } catch (e) {
      setWeatherErr(e.response?.data?.detail || t("error"));
    }
    if (isStaff) {
      try {
        const [a, b] = await Promise.all([api.get("/users", { params: { role: "admin" } }), api.get("/users", { params: { role: "staff" } })]);
        setCoaches([...a.data, ...b.data]);
      } catch { /* ignore */ }
      try {
        const pl = await api.get("/players");
        setPlayersList(pl.data);
      } catch { /* ignore */ }
      try {
        const st = await api.get(`/schedules/${s.schedule_id}/stats`);
        const m = {};
        st.data.forEach((r) => { m[r.player_id] = { goals: r.goals, assists: r.assists }; });
        setStatsForm(m);
      } catch { /* ignore */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/schedules", form);
      toast.success(t("saved"));
      if (r.data.announcement) toast.success(`${t("announcementSent")} (${r.data.announcement.recipient_count} ${t("recipientCount")})`);
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/schedules/${selected.schedule_id}/status`, { status });
      toast.success(t("saved"));
      load();
      setSelected({ ...selected, status });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const saveAttendance = async () => {
    const records = coaches.map((c) => ({ user_id: c.user_id, name: c.name, status: attendance[c.user_id] ? "present" : "absent" }));
    try {
      await api.put(`/schedules/${selected.schedule_id}/coach-attendance`, { records });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const genMatchInvoices = async () => {
    try {
      const r = await api.post("/invoices/generate-match", { schedule_id: selected.schedule_id });
      toast.success(`${r.data.created} ${t("invoicesGenerated")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const savePlayerAttendance = async () => {
    const records = playersList.filter((p) => !selected.category || p.category === selected.category)
      .map((p) => ({ player_id: p.player_id, name: p.name, present: !!pAtt[p.player_id] }));
    try {
      await api.put(`/schedules/${selected.schedule_id}/player-attendance`, { records });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const saveStats = async () => {
    const records = playersList
      .map((p) => ({ player_id: p.player_id, name: p.name, goals: Number(statsForm[p.player_id]?.goals || 0), assists: Number(statsForm[p.player_id]?.assists || 0) }))
      .filter((r) => r.goals > 0 || r.assists > 0);
    try {
      await api.post(`/schedules/${selected.schedule_id}/stats`, { records });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const remind = async () => {
    try {
      const r = await api.post(`/schedules/${selected.schedule_id}/remind`);
      setRemindLinks(r.data.wa_links);
      toast.success(`${t("announcementSent")} (${r.data.recipient_count} ${t("recipientCount")})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const sendRsvp = async (playerId, status) => {
    try {
      await api.post(`/schedules/${selected.schedule_id}/rsvp`, { player_id: playerId, status });
      setRsvpState({ ...rsvpState, [playerId]: status });
      toast.success(t("saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const notifySelected = async () => {
    const ids = Object.keys(notifySel).filter((k) => notifySel[k]);
    if (!ids.length) return;
    try {
      const r = await api.post(`/schedules/${selected.schedule_id}/notify`, { player_ids: ids });
      setNotifyLinks(r.data.wa_links);
      toast.success(`${r.data.recipient_count} ${t("recipientCount")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  const sendInquiry = async () => {
    try {
      await api.post("/inquiries", { schedule_id: selected.schedule_id, message: inquiryMsg });
      toast.success(t("saved"));
      setInquiryMsg("");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error"));
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-['Outfit'] text-2xl font-bold text-[#165A2E] md:text-3xl">{t("schedule")}</h1>
        {isStaff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-session-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                <Plus size={16} className="mr-1" /> {t("addSession")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
              <DialogHeader><DialogTitle>{t("addSession")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>{t("type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="session-type-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_KEYS).map(([v, k]) => <SelectItem key={v} value={v}>{t(k)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("category")}</Label>
                  <Select value={form.category || "all"} onValueChange={(v) => setForm({ ...form, category: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="session-category-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all")}</SelectItem>
                      {CATS.filter(Boolean).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("title")} *</Label>
                  <Input data-testid="session-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <Label>{t("date")} *</Label>
                  <Input data-testid="session-date-input" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>{t("inTime")}</Label>
                    <Input data-testid="session-start-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label>{t("outTime")}</Label>
                    <Input data-testid="session-end-input" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("location")}</Label>
                  <Input data-testid="session-location-input" value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} className="mt-1" placeholder="Padang Taman Melawati, Kuala Lumpur" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("mapUrl")}</Label>
                  <Input data-testid="session-map-input" value={form.map_embed_url} onChange={(e) => setForm({ ...form, map_embed_url: e.target.value })} className="mt-1" placeholder="https://maps.google.com/maps?q=...&output=embed" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("message")}</Label>
                  <Textarea data-testid="session-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>{t("trainingPlan")}</Label>
                  <Textarea data-testid="session-plan-input" rows={3} placeholder={t("planFocus") + " / " + t("planOutcomes")} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Button data-testid="session-submit-btn" className="w-full rounded-full bg-[#165A2E] text-white hover:bg-[#114724]">{t("save")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="schedule-filter-tabs">
        {[["all", t("all")], ["training", t("typeTraining")], ["friendly", t("typeFriendly")], ["match", t("typeMatch")], ["school", t("typeSchool")]].map(([v, l]) => (
          <button key={v} onClick={() => setTypeFilter(v)} data-testid={`filter-${v}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${typeFilter === v ? "bg-[#165A2E] text-white" : "border border-[#165A2E]/30 text-[#165A2E] hover:bg-[#165A2E]/5"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.filter((s) => typeFilter === "all" || s.type === typeFilter).map((s, i) => (
          <button key={s.schedule_id} onClick={() => openDetail(s)} data-testid={`schedule-card-${s.schedule_id}`}
            className={`gfa-card gfa-card-hover animate-fade-up stagger-${Math.min(i + 1, 4)} p-5 text-left`}>
            <div className="flex items-start justify-between gap-2">
              <span className="rounded-full bg-[#165A2E]/10 px-3 py-1 text-xs font-bold text-[#165A2E]">{t(TYPE_KEYS[s.type])}</span>
              <div className="flex gap-1.5">
                {s.status === "scheduled" && daysUntil(s.date) >= 0 && daysUntil(s.date) <= 3 && (
                  <span className="rounded-full bg-[#84CC16] px-3 py-1 text-xs font-bold text-[#0A0A0A]" data-testid={`reminder-badge-${s.schedule_id}`}>{t("soon")}</span>
                )}
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[s.status]}`} data-testid={`schedule-status-${s.schedule_id}`}>{t(s.status)}</span>
              </div>
            </div>
            <h3 className="mt-3 font-['Outfit'] text-lg font-bold text-slate-800">{s.title}</h3>
            <p className="mt-1 text-sm text-slate-500">{s.date} · {s.start_time} - {s.end_time} {s.category && `· ${s.category}`}</p>
            <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin size={13} /> {s.location_name}</p>
          </button>
        ))}
        {items.length === 0 && <p className="text-slate-400">{t("noData")}</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-3">
                  {selected.title}
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[selected.status]}`}>{t(selected.status)}</span>
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500">{selected.date} · {selected.start_time} - {selected.end_time} {selected.category && `· ${selected.category}`}</p>
              <p className="flex items-center gap-1 text-sm text-slate-600"><MapPin size={14} /> {selected.location_name}</p>
              {selected.notes && <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{selected.notes}</p>}
              {(() => {
                const catPlan = plans.find((pl) => pl.category === selected.category);
                const planText = selected.plan || (catPlan ? `${t("planFocus")}: ${catPlan.focus}\n${t("planOutcomes")}: ${catPlan.outcomes}` : "");
                return planText ? (
                  <div className="rounded-lg border border-[#84CC16]/40 bg-[#84CC16]/5 p-4" data-testid="training-plan">
                    <p className="mb-1 text-sm font-bold text-[#165A2E]">{t("trainingPlan")}</p>
                    <p className="whitespace-pre-line text-sm text-slate-600">{planText}</p>
                  </div>
                ) : null;
              })()}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {selected.map_embed_url && (
                  <div className="overflow-hidden rounded-xl border border-slate-200">
                    <iframe title="map" src={selected.map_embed_url} className="h-56 w-full" loading="lazy" data-testid="schedule-map" />
                  </div>
                )}
                <div className="rounded-xl border border-slate-200 p-4" data-testid="weather-card">
                  <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("weather")}</p>
                  {weather ? (
                    <div className="flex items-center gap-4">
                      <WeatherIcon code={weather.code} />
                      <div>
                        <p className="font-['Outfit'] text-xl font-bold text-slate-800">{weather.temp_min}°C – {weather.temp_max}°C</p>
                        <p className="text-sm text-slate-600">{lang === "ms" ? weather.label_ms : weather.label_en}</p>
                        <p className="text-xs text-slate-500">💧 {weather.precip_prob_max}%{weather.at_start ? ` · ${selected.start_time}: ${weather.at_start.temp}°C (${lang === "ms" ? weather.at_start.label_ms : weather.at_start.label_en})` : ""}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400" data-testid="weather-unavailable">{weatherErr || t("loading")}</p>
                  )}
                </div>
              </div>

              {isStaff && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Label className="mr-2">{t("setStatus")}:</Label>
                    {["scheduled", "ongoing", "cancelled", "completed"].map((st) => (
                      <Button key={st} size="sm" variant={selected.status === st ? "default" : "outline"} onClick={() => setStatus(st)}
                        data-testid={`status-btn-${st}`} className={`rounded-full text-xs ${selected.status === st ? "bg-[#165A2E] text-white" : ""}`}>
                        {t(st)}
                      </Button>
                    ))}
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("coachAttendance")}</p>
                    <div className="flex flex-wrap gap-4">
                      {coaches.map((c) => (
                        <label key={c.user_id} className="flex items-center gap-2 text-sm" data-testid={`att-coach-${c.user_id}`}>
                          <Checkbox checked={!!attendance[c.user_id]} onCheckedChange={(v) => setAttendance({ ...attendance, [c.user_id]: !!v })} />
                          {c.name}
                        </label>
                      ))}
                    </div>
                    <Button size="sm" onClick={saveAttendance} data-testid="save-attendance-btn" className="mt-3 rounded-full bg-[#165A2E] text-xs text-white">{t("save")}</Button>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("playerAttendance")}</p>
                    <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                      {playersList.filter((p) => !selected.category || p.category === selected.category).map((p) => (
                        <label key={p.player_id} className="flex items-center gap-2 text-xs" data-testid={`patt-${p.player_id}`}>
                          <input type="checkbox" checked={!!pAtt[p.player_id]} onChange={(e) => setPAtt({ ...pAtt, [p.player_id]: e.target.checked })} className="accent-[#165A2E]" />
                          {p.name}
                        </label>
                      ))}
                    </div>
                    <Button size="sm" onClick={savePlayerAttendance} data-testid="save-player-attendance-btn" className="mt-2 rounded-full bg-[#165A2E] text-xs text-white">{t("save")}</Button>
                  </div>
                  {["match", "friendly"].includes(selected.type) && (
                    <div>
                      <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("matchStats")}</p>
                      <div className="max-h-48 space-y-2 overflow-y-auto">
                        {playersList.filter((p) => !selected.category || p.category === selected.category).map((p) => (
                          <div key={p.player_id} className="flex items-center gap-2 text-sm">
                            <span className="w-36 truncate">{p.name}</span>
                            <Input type="number" min="0" className="h-8 w-20" placeholder={t("goals")} data-testid={`goals-${p.player_id}`}
                              value={statsForm[p.player_id]?.goals ?? ""} onChange={(e) => setStatsForm({ ...statsForm, [p.player_id]: { ...statsForm[p.player_id], goals: e.target.value } })} />
                            <Input type="number" min="0" className="h-8 w-20" placeholder={t("assists")} data-testid={`assists-${p.player_id}`}
                              value={statsForm[p.player_id]?.assists ?? ""} onChange={(e) => setStatsForm({ ...statsForm, [p.player_id]: { ...statsForm[p.player_id], assists: e.target.value } })} />
                          </div>
                        ))}
                      </div>
                      <Button size="sm" onClick={saveStats} data-testid="save-stats-btn" className="mt-2 rounded-full bg-[#165A2E] text-xs text-white">{t("save")}</Button>
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    {selected.type === "match" && (
                      <Button size="sm" variant="outline" onClick={genMatchInvoices} data-testid="gen-match-invoices-btn" className="rounded-full text-xs">
                        <Receipt size={13} className="mr-1" /> {t("generateMatchInvoices")}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={remind} data-testid="remind-wa-btn" className="rounded-full text-xs border-[#25D366]/50 text-[#1da851]">
                      <MessageCircle size={13} className="mr-1" /> {t("remindWhatsapp")}
                    </Button>
                  </div>
                  {remindLinks && (
                    <div className="flex flex-wrap gap-2" data-testid="remind-links">
                      {remindLinks.map((l) => (
                        <a key={l.phone + l.name} href={l.url} target="_blank" rel="noreferrer" className="rounded-full bg-[#25D366]/10 px-3 py-1 text-xs font-semibold text-[#1da851] hover:bg-[#25D366] hover:text-white">
                          {l.name}
                        </a>
                      ))}
                    </div>
                  )}
                  <div>
                    <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("notifySelected")}</p>
                    <div className="grid max-h-32 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
                      {playersList.filter((p) => !selected.category || p.category === selected.category).map((p) => (
                        <label key={p.player_id} className="flex items-center gap-2 text-xs" data-testid={`notify-${p.player_id}`}>
                          <input type="checkbox" checked={!!notifySel[p.player_id]} onChange={(e) => setNotifySel({ ...notifySel, [p.player_id]: e.target.checked })} className="accent-[#165A2E]" />
                          {p.name}
                        </label>
                      ))}
                    </div>
                    <Button size="sm" onClick={notifySelected} data-testid="notify-selected-btn" className="mt-2 rounded-full bg-[#25D366] text-xs text-white hover:bg-[#1da851]">
                      <MessageCircle size={13} className="mr-1" /> {t("send")}
                    </Button>
                    {notifyLinks && (
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="notify-links">
                        {notifyLinks.map((l) => (
                          <a key={l.phone + l.name} href={l.url} target="_blank" rel="noreferrer" className="rounded-full bg-[#25D366]/10 px-3 py-1 text-xs font-semibold text-[#1da851] hover:bg-[#25D366] hover:text-white">
                            {l.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  {(selected.rsvps?.length > 0 || Object.keys(rsvpState).length > 0) && (
                    <div>
                      <p className="mb-2 text-sm font-semibold text-[#165A2E]">{t("rsvpList")}</p>
                      <div className="space-y-1">
                        {(selected.rsvps || []).map((r) => (
                          <p key={r.player_id} className="text-xs text-slate-600" data-testid={`rsvp-item-${r.player_id}`}>
                            <span className="font-semibold">{r.player_name}</span> — {r.status === "attending" ? t("attending") : t("notAttending")} <span className="text-slate-400">({r.parent_name})</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {user.role === "parent" && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-semibold text-[#165A2E]">{t("rsvp")}</p>
                  {myPlayers.map((p) => (
                    <div key={p.player_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid={`rsvp-row-${p.player_id}`}>
                      <span className="text-sm font-medium">{p.name} <span className="text-xs text-slate-400">({p.category})</span></span>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => sendRsvp(p.player_id, "attending")} data-testid={`rsvp-yes-${p.player_id}`}
                          className={`rounded-full text-xs ${rsvpState[p.player_id] === "attending" ? "bg-[#165A2E] text-white" : "border border-[#165A2E]/30 bg-white text-[#165A2E]"}`}>
                          {t("attending")}
                        </Button>
                        <Button size="sm" onClick={() => sendRsvp(p.player_id, "not_attending")} data-testid={`rsvp-no-${p.player_id}`}
                          className={`rounded-full text-xs ${rsvpState[p.player_id] === "not_attending" ? "bg-red-500 text-white" : "border border-slate-300 bg-white text-slate-500"}`}>
                          {t("notAttending")}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {myPlayers.length === 0 && <p className="text-xs text-slate-400">{t("noData")}</p>}
                </div>
              )}

              {user.role === "parent" && (
                <div className="space-y-3 border-t pt-4">
                  <p className="text-sm font-semibold text-[#165A2E]">{t("askWeather")}</p>
                  <Textarea data-testid="inquiry-message" placeholder={t("statusTrainingQ")} value={inquiryMsg} onChange={(e) => setInquiryMsg(e.target.value)} />
                  <Button size="sm" onClick={sendInquiry} data-testid="send-inquiry-btn" className="rounded-full bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D]">
                    <Send size={13} className="mr-1" /> {t("ask")}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
